<script>
	import { scaleLinear, scalePow } from "d3-scale";

	import Sparkline from "./components/Sparkline.svelte";
	import Rule from "./components/Rule.svelte";

	import data from "./data_live/data.json";
	import dataInflation from "./data_live/inflation_2015.csv";

	import iconLebensmittel from "./icons/lebensmittel.svg";
	import iconEnergie from "./icons/energie.svg";
	import iconDienstleistungen from "./icons/dienstleistungen.svg";

	import { formatFloat } from "./utils";

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////

	const frmtItems = formatFloat(0, "%", true);

	const variable = "value";
	const colors = {
		main: "#000",
		sub: "#4545fa",
	};

	// const colorSteps = ["#B9B9B9", "#B9B9B9", "#8D57FF", "#DB5FEF", "#FF4085", "#FF2950"];
	const colorSteps = ["#B9B9B9", "#B9B9B9", "#9d65ff", "#DB5FEF", "#FF4085", "#FF2950"];

	const icons = {
		lebensmittel: iconLebensmittel,
		energie: iconEnergie,
		dienstleistungen: iconDienstleistungen,
	};

	// ///////////////////////////////////////////////////////////
	// process data
	// ///////////////////////////////////////////////////////////

	data.forEach((d) => {
		d.data = d.timeline.map((v, i) => ({ index: i, [variable]: v }));
	});

	const groups = [...new Set(data.map((d) => d.group))];

	const dataByGroups = groups.reduce(
		(obj, g) => ({
			...obj,
			[g]: data.filter((d) => d.group === g).sort((a, b) => b.change - a.change),
		}),
		{}
	);

	const yMin = data.reduce((prev, curr) => Math.min(prev, Math.min(...curr.data.map((d) => d[variable]))), Infinity);
	const yMax = data.reduce((prev, curr) => Math.max(prev, Math.max(...curr.data.map((d) => d[variable]))), -Infinity);

	const log = scalePow().exponent(0.3).domain([yMin, yMax]).range([0, 100]);
	// const logColorSub = scaleQuantize().domain([0, 100]).range(colorsTry);
	// .range([colors.sub, ...colorSteps]);

	// const logColorSub = scaleLinear()
	// 	.domain([0, 50, 100])
	// 	.range(colorSteps)
	// 	.interpolate(interpolateRgb.gamma(2.2));

	const logColorSub = scaleLinear().domain([yMin, 0, 6, 12, 55, yMax]).range(colorSteps);

	const latestPreliminary = dataInflation.at(-1).preliminary;
	$: console.log(dataInflation);
</script>

<div class="products">
	<div class="product-table">
		{#each groups as group}
			{@const dataMain = dataByGroups[group].filter((d) => d.level === "main")}
			{@const dataSub = dataByGroups[group].filter((d) => d.level === "sub")}
			<div class="group" id={group}>
				{#each dataMain as item, i}
					<div class="item {item.level}">
						<div class="icon">
							<img src={icons[group]} />
						</div>
						<div class="name">{item.name}</div>
						<div class="value">
							{frmtItems(item.change)}
						</div>
						<div class="sparkline">
							<Sparkline data={item.data} xKey="index" colors={[colors.main]} variables={[variable]} />
						</div>
					</div>
				{/each}
				<Rule />
				<div class="description long">Preissteigerungen zum Vorjahreszeitraum</div>
				<div class="description short">Preissteigerungen seit 1 Jahr</div>
				<div class="items-sub">
					{#each dataSub as item, i}
						{@const color = logColorSub(item.change)}
						<div class="item {item.level}">
							<div class="name">{item.name}</div>
							<div class="space">
								............................................................................................................
							</div>
							<div class="value" style:color>
								{frmtItems(item.change)}
							</div>
							<div class="sparkline">
								<Sparkline data={item.data} xKey="index" colors={[colors.sub]} variables={[variable]} />
							</div>
						</div>
					{/each}
				</div>
			</div>
		{/each}
	</div>
	<div class="attribution">
		Daten: Verivox (Gas Neukunden), Statistisches Bundesamt (Restliche){#if latestPreliminary}, * vorläufig{/if}
	</div>
</div>

<style lang="scss">
	@import "./main.scss";

	$column-gap: 30px;
	$column-gap-small: 17px;
	$row-gap: 40px;

	$text-width-without-margin: $text-width - 20px;

	.products {
		font-family: $ff-sans;
		font-size: $fs-l;
		line-height: $lh-l;
		color: $black;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	// > raster-width
	@media (min-width: #{$raster-width + 1px}) {
		.products,
		.attribution {
			width: 100%;
		}

		.description.short {
			display: none;
		}

		.product-table {
			width: 100%;
			column-gap: $column-gap;
		}

		.group {
			width: calc(33.33% - 0.6667 * #{$column-gap});
		}
	}

	// text-width
	@media (min-width: #{$text-width + 1px}) and (max-width: $raster-width) {
		.products {
			// margin: 0 auto;
			display: flex;
		}

		.attribution {
			width: $text-width-without-margin;
		}

		.product-table {
			width: $text-width-without-margin;
			column-gap: $column-gap-small;

			.item.main {
				font-size: 15px;
			}
		}

		.item {
			column-gap: 2px !important;
		}

		.sparkline {
			width: 13px !important;
			margin-left: 2px;
		}

		.items-sub {
			font-size: $fs-m;
			line-height: $lh-m;
		}

		.group {
			width: calc(#{$text-width-without-margin / 3} - 0.67 * #{$column-gap-small}) !important;
		}
	}

	@media (max-width: $raster-width) {
		.product-table {
			.main {
				font-size: 17px;
				line-height: 14px;
			}
		}

		.description.long {
			display: none;
		}

		.products {
			row-gap: 12px !important;
		}

		.item {
			height: 13px !important;
		}

		.icon img {
			width: 19px !important;
		}

		.space {
			margin-left: 2px !important;
		}

		.attribution {
			margin-top: 25px !important;
		}
	}

	// mobile
	@media (max-width: $text-width) {
		.product-table {
			flex-direction: column;
			row-gap: $row-gap;
			width: 100%;
		}

		.main {
			font-size: 16px;
			line-height: 14px;
		}

		.items-sub {
			font-size: $fs-s;
			line-height: $lh-s;
			row-gap: 14px !important;
			margin-top: 10px;
		}

		#energie,
		#dienstleistungen {
			.description {
				display: none;
			}
		}

		.attribution {
			text-align: left !important;
		}
	}

	.product-table {
		display: flex;
	}

	.attribution {
		opacity: 0.5;
		font-size: 14px;
		line-height: 17px;
		margin-top: 30px;
		text-align: right;
	}

	.group {
		.items-sub {
			display: flex;
			flex-direction: column;
			row-gap: 17px;
			width: 100%;
		}

		.description {
			font-size: 14px;
			line-height: 23px;
			opacity: 0.5;

			&.long {
				margin-bottom: 8px;
			}

			&.short {
				margin-bottom: 5px;
			}
		}
	}

	.item {
		display: flex;
		flex-direction: row;
		align-items: center;
		column-gap: 5px;
		height: 15px;
		white-space: nowrap;
		width: 100%;

		.icon {
			flex-shrink: 0;

			img {
				width: 30px;
				vertical-align: sub;
			}
		}

		&.main {
			font-weight: 600;
			height: 21px;

			.name {
				margin-right: auto;
			}
		}

		&.sub {
			color: #4545fa;
			font-weight: 500;
		}

		.space {
			white-space: nowrap;
			flex-grow: 1;
			overflow: hidden;
			margin-left: 6px;
			font-size: 10px;
			letter-spacing: 1.5px;
			font-family: $ff-serif;
		}

		.sparkline {
			flex-shrink: 0;
			width: 28px;
			height: 100%;
		}
	}
</style>
